import { createContext, useContext } from 'react';

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export interface ThemeContextType {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

export const THEME_STORAGE_KEY = 'onsite-theme-preference';

// The backdrop behind a scrolling page has to be the surface dark:bg-gray-900
// paints — Ergeon gray-900 from the design system — or the two disagree at the
// seam.
const DARK_BACKGROUND = '#1F1B25';
const DARK_FOREGROUND = '#F9F7FA';
// Light chrome is gray-100, not UA white: the boot shell and the error screens
// already paint it, and the body showing through ScreenPreloader must agree.
const LIGHT_BACKGROUND = '#F9F7FA';

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const getSystemTheme = (): ResolvedTheme => {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

export const getStoredTheme = (): Theme => {
  if (typeof window === 'undefined') return 'system';
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  const validThemes: Theme[] = ['light', 'dark', 'system'];
  return validThemes.includes(stored as Theme) ? (stored as Theme) : 'system';
};

export const resolveTheme = (theme: Theme): ResolvedTheme => (
  theme === 'system' ? getSystemTheme() : theme
);

export const applyThemeToDocument = (resolvedTheme: ResolvedTheme): void => {
  if (typeof window === 'undefined') return;

  const root = window.document.documentElement;
  const body = window.document.body;

  // Tailwind keys dark: variants off the `dark` class on <html>.
  root.classList.remove('light', 'dark');
  root.classList.add(resolvedTheme);

  // Native scrollbars, form controls and the overscroll gutter follow this,
  // not the Tailwind classes.
  root.style.colorScheme = resolvedTheme;

  const backgroundColor = resolvedTheme === 'dark' ? DARK_BACKGROUND : LIGHT_BACKGROUND;
  root.style.backgroundColor = backgroundColor;
  body.style.backgroundColor = backgroundColor;
  body.style.color = resolvedTheme === 'dark' ? DARK_FOREGROUND : '';
};

/**
 * Apply the stored (or system) theme before React mounts, so the customer never
 * sees a white flash on the way to a dark screen. Call it before render.
 */
export const initializeTheme = (): ResolvedTheme => {
  const resolved = resolveTheme(getStoredTheme());
  applyThemeToDocument(resolved);
  return resolved;
};

export const subscribeToSystemThemeChanges = (
  mediaQuery: MediaQueryList, callback: () => void,
): (() => void) | undefined => {
  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', callback);
    return () => mediaQuery.removeEventListener('change', callback);
  }
  if (typeof mediaQuery.addListener === 'function') {
    mediaQuery.addListener(callback);
    return () => mediaQuery.removeListener(callback);
  }
  return undefined;
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
